import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import useAdminLoad from "@/hooks/useAdminLoad";
import { Empty, LoadState, Panel } from "@/components/admin/AdminUI";
import PushLogRow from "@/components/admin/PushLogRow";
import { PUSH_CATEGORIES, PUSH_STATUS, pushCategoryLabel, pushStatus } from "@/lib/push";

const PAGE = 50;
const EMPTY = { user_id: "", category: "all", status: "all", date_from: "", date_to: "" };

function Pick({ id, label, value, onChange, keys, labelOf, testId }) {
  return (
    <div><Label htmlFor={id} className="text-xs">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id} className="mt-1 bg-white" data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all" data-testid={`${testId}-option-all`}>All</SelectItem>
          {keys.map((k) => <SelectItem key={k} value={k} data-testid={`${testId}-option-${k}`}>{labelOf(k)}</SelectItem>)}
        </SelectContent>
      </Select></div>
  );
}

export default function PushLog({ refreshKey, initialUser = "" }) {
  const [form, setForm] = useState({ ...EMPTY, user_id: initialUser });
  const [filters, setFilters] = useState({ ...EMPTY, user_id: initialUser, skip: 0 });
  const qs = new URLSearchParams({ limit: String(PAGE), skip: String(filters.skip), _r: String(refreshKey) });
  ["user_id", "date_from", "date_to"].forEach((k) => filters[k] && qs.set(k, filters[k]));
  ["category", "status"].forEach((k) => filters[k] !== "all" && qs.set(k, filters[k]));
  const { data, error, loading, reload } = useAdminLoad(`/admin/push/log?${qs}`);
  const pick = (k) => (v) => { setForm((f) => ({ ...f, [k]: v })); setFilters((f) => ({ ...f, [k]: v, skip: 0 })); };
  const total = data?.total ?? 0;

  return (
    <section data-testid="push-log-section">
      <h2 className="mb-4 text-lg font-semibold">Send log</h2>
      <form onSubmit={(e) => { e.preventDefault(); setFilters({ ...form, user_id: form.user_id.trim(), skip: 0 }); }}
        className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_180px_180px_150px_150px_auto] lg:items-end" data-testid="push-log-filters">
        <div><Label htmlFor="pl-user" className="text-xs">User ID</Label>
          <Input id="pl-user" value={form.user_id} onChange={(e) => setForm((f) => ({ ...f, user_id: e.target.value }))} className="mt-1 bg-white font-mono text-sm" data-testid="push-filter-user-input" /></div>
        <Pick id="pl-cat" label="Category" value={form.category} onChange={pick("category")} keys={data?.categories || Object.keys(PUSH_CATEGORIES)} labelOf={pushCategoryLabel} testId="push-filter-category-select" />
        <Pick id="pl-status" label="Status" value={form.status} onChange={pick("status")} keys={data?.statuses || Object.keys(PUSH_STATUS)} labelOf={(s) => pushStatus(s).label} testId="push-filter-status-select" />
        <div><Label htmlFor="pl-from" className="text-xs">From</Label><Input id="pl-from" type="date" value={form.date_from} onChange={(e) => setForm((f) => ({ ...f, date_from: e.target.value }))} className="mt-1 bg-white" data-testid="push-filter-from-input" /></div>
        <div><Label htmlFor="pl-to" className="text-xs">To</Label><Input id="pl-to" type="date" value={form.date_to} onChange={(e) => setForm((f) => ({ ...f, date_to: e.target.value }))} className="mt-1 bg-white" data-testid="push-filter-to-input" /></div>
        <div className="flex gap-2"><Button type="submit" data-testid="push-filter-apply-button">Apply</Button>
          <Button type="button" variant="ghost" onClick={() => { setForm(EMPTY); setFilters({ ...EMPTY, skip: 0 }); }} data-testid="push-filter-clear-button">Clear</Button></div>
      </form>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <Panel className="overflow-hidden">
          <p className="cw-muted border-b border-border px-5 py-3 text-xs" data-testid="push-log-total">{total} attempt{total === 1 ? "" : "s"}</p>
          {data?.items?.length ? (
            <div className="overflow-x-auto"><table className="w-full min-w-[900px] text-left" data-testid="push-log-table">
              <thead><tr className="cw-muted border-b border-border text-xs"><th className="w-12 py-3 pl-4" /><th className="py-3 pr-4 font-medium">When</th><th className="py-3 pr-4 font-medium">User</th><th className="py-3 pr-4 font-medium">Category</th><th className="py-3 pr-4 font-medium">Status</th><th className="py-3 pr-4 font-medium">Message</th><th className="py-3 pr-4 font-medium">Source</th></tr></thead>
              <tbody>{data.items.map((e) => <PushLogRow key={e.id} e={e} />)}</tbody>
            </table></div>
          ) : <Empty title="No pushes match" text="Try widening the filters." testId="push-log-empty" />}
          {total > PAGE && (
            <div className="flex items-center justify-between border-t border-border px-5 py-3 text-sm">
              <span className="cw-muted">{filters.skip + 1}–{Math.min(filters.skip + PAGE, total)} of {total}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={filters.skip === 0} onClick={() => setFilters((f) => ({ ...f, skip: f.skip - PAGE }))} data-testid="push-prev-button">Previous</Button>
                <Button size="sm" variant="outline" disabled={filters.skip + PAGE >= total} onClick={() => setFilters((f) => ({ ...f, skip: f.skip + PAGE }))} data-testid="push-next-button">Next</Button>
              </div>
            </div>
          )}
        </Panel>
      </LoadState>
    </section>
  );
}
